/**
 * @author        calvin@zumper.com (Calvin Yau)
 * @copyright     Copyright (c) 2022, Zumper
 * @description   FaqData Bizo
 */

import { RentRange, UniqueRentRangesGrouped } from '@zcode/bizos'
import type {
  AverageInfo as AverageInfoType,
  BedroomsTypes,
  FaqData as FaqDataType,
  FeaturesTypes,
  RawAverageInfo,
  RawBedrooms,
  RawFaqData,
  RawFeatures,
  RawUniqueBedrooms,
  UniqueBedroomsTypes,
} from '@zcode/state-type'

import { Bizo, autoclass } from '../../shared/bizos/base'

const BASE_FAQ_DATA_DEFINITION = {
  averageBathrooms: 'average_bathrooms',
  averageRent: 'average_rent',
  medianRent: 'median_rent',
  averageSft: 'average_sft',
  countListings: 'count_listings',
  countListingsWithPhotos: 'count_listings_with_photos',
  highestHalfBathrooms: 'highest_half_bathrooms',
  highestPrice: 'highest_price',
  highestSft: 'highest_sft',
  lowestPrice: 'lowest_price',
  lowestSft: 'lowest_sft',
  maxBedrooms: 'max_bedrooms',
  minBedrooms: 'min_bedrooms',
  yoyAverageRentDifference: 'yoy_average_rent_difference',
  yoyAverageRentPercent: 'yoy_average_rent_percent',
} as const

export const BaseFaqData = autoclass<AverageInfoType, RawAverageInfo>(
  'BaseFaqData',
  class BaseFaqData extends Bizo<AverageInfoType, RawAverageInfo> {},
  BASE_FAQ_DATA_DEFINITION
)
export type BaseFaqData = InstanceType<typeof BaseFaqData>

const BEDROOMS_DEFINITION = {
  studio: { name: 'STUDIO', type: BaseFaqData },
  oneBed: { name: '1_BED', type: BaseFaqData },
  twoBed: { name: '2_BED', type: BaseFaqData },
  threeBed: { name: '3_BED', type: BaseFaqData },
  fourPlusBed: { name: '4+_BED', type: BaseFaqData },
} as const

export const Bedrooms = autoclass<UniqueBedroomsTypes, RawUniqueBedrooms>(
  'Bedrooms',
  class Bedrooms extends Bizo<UniqueBedroomsTypes, RawUniqueBedrooms> {},
  BEDROOMS_DEFINITION
)
export type Bedrooms = InstanceType<typeof Bedrooms>

const FEATURES_BASE_DEFINITION = {
  averageBathrooms: 'average_bathrooms',
  averageRent: 'average_rent',
  averageSft: 'average_sft',
  countListings: 'count_listings',
  countListingsWithPhotos: 'count_listings_with_photos',
  fourPlusBed: { name: '4+_BED', type: BaseFaqData },
  highestHalfBathrooms: 'highest_half_bathrooms',
  highestPrice: 'highest_price',
  highestSft: 'highest_sft',
  lowestPrice: 'lowest_price',
  lowestSft: 'lowest_sft',
  maxBedrooms: 'max_bedrooms',
  medianRent: 'median_rent',
  minBedrooms: 'min_bedrooms',
  oneBed: { name: '1_BED', type: BaseFaqData },
  studio: { name: 'STUDIO', type: BaseFaqData },
  threeBed: { name: '3_BED', type: BaseFaqData },
  twoBed: { name: '2_BED', type: BaseFaqData },
  yoyAverageRentDifference: 'yoy_average_rent_difference',
  yoyAverageRentPercent: 'yoy_average_rent_percent',
} as const

export const FeaturesBase = autoclass<BedroomsTypes, RawBedrooms>(
  'FeaturesBase',
  class FeaturesBase extends Bizo<BedroomsTypes, RawBedrooms> {},
  FEATURES_BASE_DEFINITION,
  null
)
export type FeaturesBase = InstanceType<typeof FeaturesBase>

const FEATURES_DEFINITION = {
  airConditioning: { name: 'AIR_CONDITIONING', type: FeaturesBase },
  cheap: { name: 'CHEAP', type: FeaturesBase },
  dishwasher: { name: 'DISHWASHER', type: FeaturesBase },
  furnished: { name: 'FURNISHED', type: FeaturesBase },
  garage: { name: 'GARAGE', type: FeaturesBase },
  inUnitLaundry: { name: 'IN_UNIT_LAUNDRY', type: FeaturesBase },
  luxury: { name: 'LUXURY', type: FeaturesBase },
  lowIncome: { name: 'LOW_INCOME', type: FeaturesBase },
  moveInSpecial: { name: 'MOVEIN_SPECIAL', type: FeaturesBase },
  noFee: { name: 'NO_FEE', type: FeaturesBase },
  outdoorSpace: { name: 'OUTDOOR_SPACE', type: FeaturesBase },
  petFriendly: { name: 'PET_FRIENDLY', type: FeaturesBase },
  utilitiesIncluded: { name: 'UTILITIES_INCLUDED', type: FeaturesBase },
} as const

export const Features = autoclass<FeaturesTypes, RawFeatures>(
  'Features',
  class Features extends Bizo<FeaturesTypes, RawFeatures> {},
  FEATURES_DEFINITION
)
export type Features = InstanceType<typeof Features>

export {
  /** @deprecated import { RentRange } from '@zcode/bizos' instead */
  RentRange,
  /** @deprecated import { UniqueRentRangesGrouped } from '@zcode/bizos' instead */
  UniqueRentRangesGrouped as RentRangesGrouped,
}

const FAQ_DATA_DEFINITION = {
  averageBathrooms: 'average_bathrooms',
  averageRent: 'average_rent',
  averageSft: 'average_sft',
  countListings: 'count_listings',
  countListingsWithPhotos: 'count_listings_with_photos',
  highestHalfBathrooms: 'highest_half_bathrooms',
  highestPrice: 'highest_price',
  highestSft: 'highest_sft',
  lowestPrice: 'lowest_price',
  lowestSft: 'lowest_sft',
  maxBedrooms: 'max_bedrooms',
  minBedrooms: 'min_bedrooms',
  yoyAverageRentDifference: 'yoy_average_rent_difference',
  yoyAverageRentPercent: 'yoy_average_rent_percent',
  rentRangesGrouped: { name: 'rent_ranges_grouped', type: UniqueRentRangesGrouped },
  bedrooms: { name: 'BEDROOMS', type: Bedrooms },
  features: { name: 'FEATURES', type: Features },
} as const

export const FaqData = autoclass<FaqDataType, RawFaqData>(
  'FaqData',
  class FaqData extends Bizo<FaqDataType, RawFaqData> {},
  FAQ_DATA_DEFINITION
)
export type FaqData = InstanceType<typeof FaqData>

export default FaqData
